import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Box, IconButton, Typography } from '@mui/material';
import { memo } from 'react';
import type { StakeErrorType } from '../../../../@types';
import { removeBetslipBet } from '../../../../stores/betslip/selectors';
import type { BetslipEvent } from '../../../../types/betslip';
import type { NeobetBetslipDrawerController } from '../../../../hooks/components/useNeobetBetslipDrawer';
import { useBetBuilderItem } from '../../../../hooks/useBetBuilderItem';
import useLocalization from '../../../../hooks/useLocalization';
import { WalletType } from '../../../../types/enums';
import BetBuilderBadge from '../../../atoms/BetBuilderBadge';
import OddsDisplay from '../../../atoms/OddsDisplay';
import BetBuilderLegList from '../../../molecules/BetBuilderLegList';
import NeobetRemoveRail from '../atoms/NeobetRemoveRail';
import LiveDot from '../atoms/LiveDot';
import NeobetBetslipStakeInput from './NeobetBetslipStakeInput';
import NeobetBetslipStakeMessage from './NeobetBetslipStakeMessage';

const styles = {
  root: (isDisabled: boolean, showStake: boolean) => ({
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: showStake ? '44px minmax(0, 1fr) 104px' : '44px minmax(0, 1fr)',
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'info.200',
    backgroundColor: 'neutral.600',
    overflow: 'hidden',
    opacity: isDisabled ? 0.5 : 1,
  }),
  body: {
    minWidth: 0,
    display: 'grid',
    alignContent: 'center',
    gap: 0.625,
    px: 1.25,
    py: 1,
  },
  betMeta: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    textAlign: 'left',
  },
  betMetaText: {
    minWidth: 0,
    display: 'grid',
    gap: 0.25,
  },
  title: {
    typography: 'body2',
    fontWeight: 800,
    color: 'text.primary',
    lineHeight: 1.25,
  },
  eventName: {
    typography: 'subtitle3',
    color: 'neutral.200',
    lineHeight: 1.25,
  },
  topRight: {
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
  },
  betBuilderBadge: {
    height: 22,
    borderTopRightRadius: 10,
    borderBottomLeftRadius: 10,
    px: 1.5,
    backgroundColor: 'info.200',
    color: 'neutral.900',
    typography: 'caption',
    fontWeight: 700,
  },
  legs: {
    mt: 0.25,
  },
  odds: {
    typography: 'body1',
    color: 'primary.main',
    fontWeight: 800,
    justifySelf: 'start',
  },
  removeLegButton: {
    p: 0.25,
    color: 'neutral.300',
    alignSelf: 'flex-start',
  },
  removeIcon: {
    fontSize: 16,
    color: 'error.200',
  },
  stakeColumn: {
    boxSizing: 'border-box',
    display: 'grid',
    gap: 0.375,
    justifyItems: 'stretch',
    alignContent: 'end',
    minWidth: 0,
    width: 1,
    px: 1,
    py: 1,
    '& [data-testid^="neobet-bet-builder-stake-message-"]': {
      maxWidth: 88,
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    },
  },
} as const;

type NeobetBetBuilderBetslipItemProps = {
  bet: BetslipEvent;
  selectedWallet: WalletType;
  stakeErrorType?: StakeErrorType;
  handleStakeChange: NeobetBetslipDrawerController['handleStakeChange'];
  removeSingleStakeError: NeobetBetslipDrawerController['removeSingleStakeError'];
  updateSinglesStakeAmount: NeobetBetslipDrawerController['updateSinglesStakeAmount'];
  showStakeInput?: boolean;
};

const NeobetBetBuilderBetslipItemBase = ({
  bet,
  selectedWallet,
  stakeErrorType,
  handleStakeChange,
  removeSingleStakeError,
  updateSinglesStakeAmount,
  showStakeInput = true,
}: NeobetBetBuilderBetslipItemProps) => {
  const { t } = useLocalization();
  const { legs } = useBetBuilderItem({ type: 'betslip', bet });
  const isDisabled = Boolean(bet.disabled);

  if (!bet.betBuilder) {
    return null;
  }

  return (
    <Box sx={styles.root(isDisabled, showStakeInput)} data-testid={`neobet-bet-builder-item-${bet.eventId}`}>
      <NeobetRemoveRail
        onClick={() => {
          removeBetslipBet(bet.outcomeId);
          removeSingleStakeError(bet.outcomeId);
        }}
        ariaLabel={t('remove')}
        testId={`neobet-remove-bet-builder-group-${bet.eventId}`}
      />

      <Box sx={styles.body}>
        <Box sx={styles.betMeta}>
          <Box sx={styles.betMetaText}>
            <Typography sx={styles.title}>{t('betBuilder')}</Typography>
            <Typography sx={styles.eventName}>{bet.eventName}</Typography>
          </Box>
        </Box>
        <Box sx={styles.topRight}>
          {bet.isLive && <LiveDot size={8} />}
          <BetBuilderBadge sx={styles.betBuilderBadge} />
        </Box>

        <Box sx={styles.legs}>
          <BetBuilderLegList
            legs={legs}
            variant='neobet'
            renderAction={(leg) => (
              <IconButton
                key={leg.outcomeId}
                size='small'
                sx={styles.removeLegButton}
                onClick={() => {
                  removeBetslipBet(leg.outcomeId);
                  removeSingleStakeError(bet.outcomeId);
                }}
                aria-label={t('remove')}
                data-testid={`neobet-remove-bet-builder-leg-${leg.outcomeId}`}
              >
                <DeleteForeverIcon sx={styles.removeIcon} />
              </IconButton>
            )}
          />
        </Box>

        <OddsDisplay value={bet.odds} sx={styles.odds} data-testid={`neobet-bet-builder-odds-${bet.eventId}`} />
      </Box>

      {showStakeInput ? (
        <Box sx={styles.stakeColumn}>
          <NeobetBetslipStakeInput
            value={bet.singlesStakeAmount?.toString() ?? ''}
            width='100%'
            disabled={isDisabled}
            helperMessage={
              stakeErrorType ? (
                <NeobetBetslipStakeMessage
                  errorType={stakeErrorType}
                  ticketType={bet.isLive ? 'inPlay' : 'preMatch'}
                  selectedWallet={selectedWallet}
                  testId={`neobet-bet-builder-stake-message-${bet.outcomeId}`}
                />
              ) : undefined
            }
            onChange={(value) =>
              handleStakeChange(
                bet.outcomeId,
                value,
                updateSinglesStakeAmount,
                true,
                bet.isLive ? 'inPlay' : 'preMatch',
                1
              )
            }
            testId={`neobet-bet-builder-stake-${bet.outcomeId}`}
          />
        </Box>
      ) : null}
    </Box>
  );
};

const NeobetBetBuilderBetslipItem = memo(NeobetBetBuilderBetslipItemBase);

export default NeobetBetBuilderBetslipItem;
